import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Zap, Download, Info, Users, Phone } from "lucide-react";
import { ApolloFilters } from "@/pages/apollo/ApolloFilters";
import { ApolloResultsTable } from "@/pages/apollo/ApolloResultsTable";
import { ApolloSavedPresets } from "@/pages/apollo/ApolloFilters";

/**
 * Apollo.io broad B2B search — orchestrator only. Rendering blocks live in
 * `/pages/apollo/*.jsx`; this component owns state + API calls.
 */
export default function ApolloSearch() {
  const [industry, setIndustry] = useState("plumbing");
  const [stateName, setStateName] = useState("California");
  const [cityInput, setCityInput] = useState("");
  const [employees, setEmployees] = useState("1,10");
  const [noWebsite, setNoWebsite] = useState(true);
  const [perPage, setPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [results, setResults] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [counts, setCounts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState({});
  const [importing, setImporting] = useState(false);
  const [calling, setCalling] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [savedSearches, setSavedSearches] = useState([]);
  const [savingSearch, setSavingSearch] = useState(false);

  const loadSavedSearches = async () => {
    try {
      const { data } = await api.get("/apollo/saved-searches");
      setSavedSearches(data.saved_searches || []);
    } catch { /* silent — feature is optional */ }
  };
  useEffect(() => { loadSavedSearches(); }, []);

  const applySavedSearch = (s) => {
    const f = s.filters || {};
    if (f.industry !== undefined) setIndustry(f.industry);
    if (f.stateName !== undefined) setStateName(f.stateName);
    if (f.cityInput !== undefined) setCityInput(f.cityInput);
    if (f.location !== undefined) {
      const parts = String(f.location).split(",").map((x) => x.trim());
      if (parts.length >= 2) { setCityInput(parts[0]); setStateName(parts[1]); }
      else if (parts.length === 1) { setStateName(parts[0]); }
    }
    if (f.employees !== undefined) setEmployees(f.employees);
    if (typeof f.noWebsite === "boolean") setNoWebsite(f.noWebsite);
    if (f.perPage) setPerPage(f.perPage);
    setPage(1);
    toast.success(`Loaded preset "${s.name}"`);
  };

  const currentFilters = () => ({ industry, stateName, cityInput, employees, noWebsite, perPage });

  const saveCurrentSearch = async () => {
    const name = window.prompt("Name this preset (e.g. \"CA micro plumbers, no site\"):", "");
    if (!name || !name.trim()) return;
    setSavingSearch(true);
    try {
      await api.post("/apollo/saved-searches", { name: name.trim(), filters: currentFilters() });
      toast.success(`Saved "${name.trim()}"`);
      await loadSavedSearches();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingSearch(false); }
  };

  const deleteSavedSearch = async (id, name) => {
    if (!window.confirm(`Delete preset "${name}"?`)) return;
    try {
      await api.delete(`/apollo/saved-searches/${id}`);
      toast.success("Deleted");
      setSavedSearches((ss) => ss.filter((s) => s.id !== id));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const enrichSelected = async () => {
    const chosen = results.filter((r) => selected[r.id]);
    if (chosen.length === 0) return toast.error("Select at least one row to enrich.");
    setEnriching(true);
    try {
      const { data } = await api.post("/apollo/enrich", {
        orgs: chosen.map((r) => ({
          apollo_organization_id: r.apollo_organization_id,
          name: r.name,
          primary_domain: r.primary_domain,
          website: r.website,
        })),
      });
      if (data.plan_locked) {
        toast.error("Apollo /people/match needs a paid plan. Upgrade at apollo.io/pricing to unlock owner names & emails.", { duration: 8000 });
        return;
      }
      const byOrg = {};
      (data.enriched || []).forEach((e) => { byOrg[e.apollo_organization_id] = e; });
      setResults((rs) => rs.map((r) => {
        const e = byOrg[r.apollo_organization_id];
        return e ? { ...r, contact_name: e.contact_name, title: e.title, email: e.email, linkedin_url: e.linkedin_url || r.linkedin_url } : r;
      }));
      const filled = (data.enriched || []).filter((e) => e.contact_name || e.email).length;
      toast.success(`Enriched ${filled} of ${chosen.length}${data.applied_to_leads ? ` · updated ${data.applied_to_leads} imported lead${data.applied_to_leads === 1 ? "" : "s"}` : ""}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setEnriching(false); }
  };

  const runSearch = async (e, overridePage) => {
    e?.preventDefault?.();
    const nextPage = overridePage ?? page;
    setBusy(true);
    setSelected({});
    try {
      const body = {
        industry_keywords: industry.split(",").map(s => s.trim()).filter(Boolean),
        organization_locations: [cityInput.trim() ? `${cityInput.trim()}, ${stateName}, US` : `${stateName}, US`],
        employee_ranges: [employees],
        no_website_only: noWebsite,
        page: nextPage,
        per_page: perPage,
      };
      const { data } = await api.post("/apollo/search", body);
      setResults(data.results || []);
      setPagination(data.pagination || null);
      setCounts(data.counts || null);
      setPage(nextPage);
      if (!data.results?.length) toast.info("No results — try broadening filters or turning off 'no website only'.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const toggleSelect = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleAll = () => {
    const eligible = results.filter((r) => !r.already_imported);
    const allOn = eligible.length > 0 && eligible.every((r) => selected[r.id]);
    if (allOn) return setSelected({});
    const next = {};
    eligible.forEach((r) => { next[r.id] = true; });
    setSelected(next);
  };

  const importSelected = async () => {
    const chosen = results.filter((r) => selected[r.id] && !r.already_imported);
    if (chosen.length === 0) return toast.error("Select at least one lead to import.");
    setImporting(true);
    const CHUNK = 10;
    const toastId = toast.loading(`Importing 0 / ${chosen.length}…`);
    let totalImported = 0, totalSkipped = 0;
    const importedIds = [];
    try {
      for (let i = 0; i < chosen.length; i += CHUNK) {
        const batch = chosen.slice(i, i + CHUNK);
        const { data } = await api.post("/apollo/import", { leads: batch });
        totalImported += data.imported || 0;
        totalSkipped += data.skipped || 0;
        importedIds.push(...(data.imported_ids || []));
        toast.loading(`Importing ${Math.min(i + CHUNK, chosen.length)} / ${chosen.length}…`, { id: toastId });
      }
      toast.success(
        `Imported ${totalImported} lead${totalImported === 1 ? "" : "s"}${totalSkipped ? ` · skipped ${totalSkipped} duplicate${totalSkipped === 1 ? "" : "s"}` : ""}`,
        { id: toastId }
      );
      const chosenIds = new Set(chosen.map((r) => r.id));
      setResults((rs) => rs.map((r) => chosenIds.has(r.id) ? { ...r, already_imported: true, _leadId: importedIds.shift() } : r));
      setSelected({});
    } catch (err) { toast.error(formatApiError(err), { id: toastId }); }
    finally { setImporting(false); }
  };

  const bulkCallSelected = async () => {
    const chosen = results.filter((r) => selected[r.id] && (r.phone || "").trim());
    if (chosen.length === 0) return toast.error("Select at least one row with a phone number.");
    if (!window.confirm(`Import ${chosen.length} Apollo lead${chosen.length === 1 ? "" : "s"} and fire AI voice calls? Each call costs ~$0.11/min.`)) return;
    setCalling(true);
    const toastId = toast.loading(`Importing ${chosen.length} leads before dialling…`);
    try {
      await api.post("/apollo/import", { leads: chosen });
      const orgIds = chosen.map((r) => r.apollo_organization_id).filter(Boolean);
      const { data: leadsData } = await api.get("/leads?limit=500");
      const idByOrg = {};
      (leadsData.leads || []).forEach((l) => { if (l.apollo_organization_id) idByOrg[l.apollo_organization_id] = l.id; });
      const allLeadIds = orgIds.map((oid) => idByOrg[oid]).filter(Boolean);
      if (allLeadIds.length === 0) throw new Error("No matching leads found after import");
      toast.loading(`Dialling ${allLeadIds.length} lead${allLeadIds.length === 1 ? "" : "s"} with AI…`, { id: toastId });
      const { data: bulk } = await api.post("/vapi/call/bulk", { lead_ids: allLeadIds });
      const q = bulk?.counts?.queued ?? 0, s = bulk?.counts?.skipped ?? 0;
      toast.success(`Queued ${q} AI call${q === 1 ? "" : "s"}${s ? ` · skipped ${s}` : ""}. Track live at /outreach`, { id: toastId });
      const chosenIds = new Set(chosen.map((r) => r.id));
      setResults((rs) => rs.map((r) => chosenIds.has(r.id) ? { ...r, already_imported: true } : r));
      setSelected({});
    } catch (err) { toast.error(formatApiError(err), { id: toastId }); }
    finally { setCalling(false); }
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="p-10" data-testid="apollo-search-page">
      <Toaster position="top-right" richColors />
      <div className="mb-8">
        <div className="overline mb-3 flex items-center gap-2">
          <Zap className="w-3.5 h-3.5 text-orange-500" /> Apollo.io · Broad B2B search
        </div>
        <h1 className="font-display text-4xl font-black tracking-tighter mb-2">
          Find any small business, anywhere
        </h1>
        <p className="text-neutral-500 max-w-xl">
          Search 28M+ companies with phone numbers, employee counts, revenue estimates,
          and LinkedIn URLs. Ideal for micro-shops Google Places under-covers.
        </p>
      </div>

      <div className="mb-6 flex items-start gap-2 text-xs text-blue-900 bg-blue-50 border border-blue-200 p-3 max-w-3xl">
        <Info className="w-4 h-4 mt-0.5 text-blue-700 flex-shrink-0" />
        <div>
          <strong>Free-plan mode:</strong> returns company records only (owner emails require Apollo paid plan).
          Use the phone number and LinkedIn to start outreach — every imported lead flows into your existing Pipeline.
          Upgrade to unlock the <strong>Enrich contacts</strong> button below.
        </div>
      </div>

      <ApolloSavedPresets
        presets={savedSearches}
        onApply={applySavedSearch}
        onDelete={deleteSavedSearch}
        onSave={saveCurrentSearch}
        saving={savingSearch}
      />

      <ApolloFilters
        industry={industry} setIndustry={setIndustry}
        stateName={stateName} setStateName={setStateName}
        cityInput={cityInput} setCityInput={setCityInput}
        employees={employees} setEmployees={setEmployees}
        perPage={perPage} setPerPage={setPerPage}
        noWebsite={noWebsite} setNoWebsite={setNoWebsite}
        onSubmit={runSearch} busy={busy}
      />

      {counts && (
        <div className="mb-4 flex items-center gap-3 text-xs text-neutral-500 flex-wrap">
          <span className="pill pill-interested">Live · Apollo.io</span>
          <span className="font-mono">
            Page {pagination?.page ?? page}
            {pagination?.total_pages ? ` of ${Math.min(pagination.total_pages, 500)}` : ""}
            {pagination?.total_entries ? ` · ${pagination.total_entries.toLocaleString()} total matches` : ""}
          </span>
          <span className="font-mono">Returned: {counts.returned} · After no-website filter: {counts.after_no_website_filter}</span>
        </div>
      )}

      {results.length > 0 && (
        <>
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="overline mb-1">{results.length} matching leads</div>
              <h2 className="font-display text-xl font-semibold">Select and import to Pipeline</h2>
            </div>
            <div className="flex items-center gap-3">
              {selectedCount > 0 && (
                <>
                  <button data-testid="apollo-bulk-call-btn" onClick={bulkCallSelected} disabled={calling || importing}
                          className="px-4 py-2 bg-[#E24E1B] text-white font-semibold text-sm flex items-center gap-2 hover:bg-[#0F2340] transition disabled:opacity-50"
                          title="Import and start AI voice calls to selected leads">
                    <Phone className="w-4 h-4" />
                    {calling ? "Dialling…" : `Call ${selectedCount} with AI`}
                  </button>
                  <button data-testid="apollo-enrich-btn" onClick={enrichSelected} disabled={enriching}
                          className="px-4 py-2 border border-orange-500 text-orange-600 font-semibold text-sm flex items-center gap-2 hover:bg-orange-50 transition disabled:opacity-50"
                          title="Fetch owner name + email via Apollo /people/match (paid plan)">
                    <Users className="w-4 h-4" />
                    {enriching ? "Enriching…" : `Enrich ${selectedCount}`}
                  </button>
                  <button data-testid="apollo-import-btn" onClick={importSelected} disabled={importing || calling}
                          className="px-4 py-2 bg-black text-white font-semibold text-sm flex items-center gap-2 hover:bg-neutral-800 transition disabled:opacity-50">
                    <Download className="w-4 h-4" />
                    {importing ? "Importing…" : `Import ${selectedCount} to CRM`}
                  </button>
                </>
              )}
              <div className="flex items-center gap-1">
                <button data-testid="apollo-prev-btn" onClick={(e) => runSearch(e, Math.max(1, page - 1))} disabled={busy || page <= 1}
                        className="lf-btn-outline text-xs px-3 py-1.5 disabled:opacity-40">Prev</button>
                <button data-testid="apollo-next-btn" onClick={(e) => runSearch(e, page + 1)}
                        disabled={busy || (pagination && pagination.total_pages && page >= pagination.total_pages)}
                        className="lf-btn-outline text-xs px-3 py-1.5 disabled:opacity-40">Next</button>
              </div>
            </div>
          </div>

          <ApolloResultsTable results={results} selected={selected} onToggle={toggleSelect} onToggleAll={toggleAll} />
        </>
      )}

      {results.length === 0 && !busy && (
        <div className="border border-dashed border-neutral-300 p-16 text-center">
          <div className="overline mb-3">Ready to search</div>
          <p className="text-neutral-500 max-w-md mx-auto">
            Tune your filters and hit search. Apollo indexes far more small businesses than
            Google Places — especially micro-shops under 10 employees.
          </p>
        </div>
      )}
    </div>
  );
}
